import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal } from "@angular/core";
import { S25DateModule } from "../../s25-dateformat/s25.date.module";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25States } from "../../../services/state/state.map";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { SeEvent } from "../event/se.event.details.view.component";
import { s25Resource } from "../../../util/s25.resource";
import { StudentEngagementService } from "../student.engagement.service";
import { transformLogoDisplayUrl } from "../logo-url.coordinator";
import { Item } from "../../../pojo/Item";
import { UserStore } from "../../../stores/user.store";
import { SeOwnAttendanceStateComponent } from "../attendance/se.own.attendance.state.component";
import { LoginStore } from "../../../stores/login.store";
import { attendanceState, AttendanceState } from "../attendance/attendance.service";
import { Attendance } from "../attendance/se.attendance.util";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";

@TypeManagerDecorator("se-event-tile")
@Component({
    selector: "se-event-tile",
    template: `
        <a
            s25-ng-router-link
            [stateName]="routingStateName"
            [stateParams]="stateParam()"
            [attr.aria-label]="'View event: ' + event().itemName"
        >
            <article class="event-tile-wrapper" [attr.aria-labelledby]="'event-name-' + event().itemId">
                <div class="event-image-container">
                    @if (tileOccurrence() && loginStore.loggedIn()) {
                        <s25-ng-se-own-attendance-state
                            [(state)]="tileOccurrence()!.attendanceState!"
                            [eventId]="event().itemId"
                            [occurrenceId]="tileOccurrence()!.reservationId"
                            [ariaLabel]="attendanceStateLabel(tileOccurrence()!.attendanceState)"
                            (click)="$event.preventDefault(); $event.stopPropagation()"
                        />
                    }
                    @if (imageUrl()) {
                        <img
                            [src]="imageUrl()"
                            [alt]="event().itemName + ' event image'"
                            class="event-image"
                            (error)="imageUrl.set(undefined)"
                        />
                    } @else {
                        <div class="event-image-placeholder" aria-hidden="true">
                            <s25-ng-icon [type]="'calendarDate'" [size]="60" [ariaLabel]="false" />
                        </div>
                    }
                    <time class="event-date-overlay" [attr.datetime]="isoDate()" aria-hidden="true">
                        <span class="date-day">{{ getDay() }}</span>
                        <span class="date-month">{{ getMonth() }}</span>
                    </time>
                </div>

                <div class="event-content">
                    @if (event().hostOrg) {
                        <div class="event-host-org">
                            @if (hostOrgLogoUrl()) {
                                <img
                                    class="event-host-org-logo"
                                    [src]="hostOrgLogoUrl()"
                                    [alt]="event().hostOrg!.itemName + ' logo'"
                                    (error)="hostOrgLogoUrl.set(undefined)"
                                />
                            }
                            <span class="event-host-org-name">{{ event().hostOrg!.itemName }}</span>
                        </div>
                    }
                    @if (event().additionalOrganizations?.length) {
                        <div class="event-additional-orgs">
                            @for (org of event().additionalOrganizations!; track org.itemId) {
                                <span class="event-additional-org">{{ org.itemName }}</span>
                            }
                        </div>
                    }

                    <h3 [id]="'event-name-' + event().itemId" class="event-name">{{ event().itemName }}</h3>

                    <div class="event-datetime">
                        <s25-ng-icon [type]="'calendarDate'" [size]="16" [ariaLabel]="false" />
                        <time class="datetime-text" [attr.datetime]="isoDate()">
                            {{ datetimeLabel() | datePipe: datePref() }}
                        </time>
                    </div>

                    @if (event().description) {
                        <div class="event-description" [innerHTML]="event().description | safeHTML"></div>
                    }

                    @if (event().locations?.length) {
                        <div class="event-location">
                            <s25-ng-icon [type]="'locationMarker'" [size]="16" [ariaLabel]="false" />
                            <span class="location-text">{{ event().locations!.join(", ") }}</span>
                        </div>
                    }
                </div>
            </article>
        </a>
    `,
    styles: `
        .event-tile-wrapper {
            background: var(--color--surface-bg, white);
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
            transition:
                transform var(--transition-medium, 200ms) ease,
                box-shadow var(--transition-medium, 200ms) ease;
            border: 1px solid var(--color--border, rgba(0, 0, 0, 0.15));
            display: flex;
            flex-direction: column;
        }

        a,
        .event-tile-wrapper {
            display: block;
            height: 100%;
        }

        a:hover .event-tile-wrapper,
        a:focus .event-tile-wrapper {
            transform: translateY(-4px);
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
        }

        a:focus {
            outline: none;
            border-radius: 16px;
        }

        a:focus .event-tile-wrapper {
            box-shadow:
                0 8px 30px rgba(0, 0, 0, 0.15),
                var(--shadow--form-focus, 0 0 0 2px #fafaf9, 0 0 0 4px #2573a7);
        }

        .event-image-container {
            position: relative;
            height: 200px;
            overflow: hidden;
        }

        .event-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .event-image-placeholder {
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .event-date-overlay {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 0.5rem;
            border-radius: 8px;
            text-align: center;
            min-width: 50px;
        }

        .date-day {
            display: block;
            font-size: 1.2rem;
            font-weight: 700;
            line-height: 1;
        }

        .date-month {
            display: block;
            font-size: 0.8rem;
            text-transform: uppercase;
            opacity: 0.9;
            margin-top: 0.25rem;
        }

        .event-content {
            padding: 1.5rem;
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .event-host-org {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }

        .event-host-org-logo {
            flex-shrink: 0;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            object-fit: cover;
        }

        .event-host-org-name {
            font-size: 0.85rem;
            font-weight: 600;
            color: var(--color--text);
        }

        .event-additional-orgs {
            display: flex;
            flex-wrap: wrap;
            gap: 0.35rem;
            margin-bottom: 0.75rem;
        }

        .event-additional-org {
            font-size: 0.75rem;
            padding: 0.15rem 0.5rem;
            border-radius: 999px;
            background: #f0f0f0;
            color: #555;
        }

        .event-name {
            margin: 0 0 1rem 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--color--text, #333);
            line-height: 1.3;
            word-wrap: break-word;
        }

        .event-datetime {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
            color: var(--color--text-muted, #666);
        }

        .datetime-text {
            font-weight: 500;
        }

        .event-description {
            color: var(--color--text, #333);
            line-height: 1.5;
            margin: 0 0 1rem 0;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .event-location {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: var(--color--text-muted, #666);
            font-size: 0.9rem;
        }

        .location-text {
            font-weight: 500;
        }

        ::ng-deep .event-image-placeholder svg {
            color: #fff;
            opacity: 0.8;
        }

        ::ng-deep .event-datetime svg,
        ::ng-deep .event-location svg {
            opacity: 0.7;
        }

        s25-ng-se-own-attendance-state {
            --icon-color: white;
            position: absolute;
            padding: 1rem;
        }

        @media (max-width: 768px) {
            .event-content {
                padding: 1rem;
            }

            .event-name {
                font-size: 1.2rem;
            }

            .event-image-container {
                height: 160px;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25DateModule, S25IconComponent, S25RouterLinkDirective, SeOwnAttendanceStateComponent, S25PipesModule],
})
export class SeEventTileComponent {
    event = input.required<SeEvent>();

    private readonly userStore = inject(UserStore);
    protected readonly loginStore = inject(LoginStore);

    protected readonly datePref = computed(() => this.userStore.dateTimeFormat());

    protected readonly tileOccurrence = computed(() => {
        const reservationId = this.event().reservationId ?? this.event().occurrences?.[0]?.reservationId;
        if (!reservationId) {
            return undefined;
        }
        const fromList = this.event().occurrences?.find((occurrence) => occurrence.reservationId === reservationId);
        return (
            fromList ?? {
                reservationId,
                attendanceState: 0 as AttendanceState,
            }
        );
    });

    protected readonly datetimeLabel = computed(() => this.event().date);

    stateParam = computed((): S25States.ParamsForName<"studentEngagementEventDetails"> | undefined => {
        if (this.event().itemId) {
            return { itemId: this.event().itemId! } as S25States.ParamsForName<"studentEngagementEventDetails">;
        }
    });
    routingStateName = S25States.names.studentEngagementEventDetails;

    protected imageUrl = linkedSignal(() => this.imageUrlGet.value());

    protected hostOrgLogoUrl = linkedSignal(() => this.hostOrgLogoGet.value());

    private readonly imageUrlGet = s25Resource({
        loader: async () => {
            const existingUrl = transformLogoDisplayUrl(this.event().imageUrl);
            if (existingUrl) {
                return existingUrl;
            }
            if (this.event().logosResolvedFromSearch) {
                return undefined;
            }
            return StudentEngagementService.getLogoUrl(Item.Ids.Event, this.event().itemId).catch((error) => {
                throw error;
            });
        },
    });

    private readonly hostOrgLogoGet = s25Resource({
        if: () => !!this.event().hostOrg?.itemId,
        loader: async () => {
            const existingUrl = transformLogoDisplayUrl(this.event().hostOrg?.logoUrl);
            if (existingUrl) {
                return existingUrl;
            }
            if (this.event().hostOrg?.logosResolvedFromSearch ?? this.event().logosResolvedFromSearch) {
                return undefined;
            }
            const hostOrgId = this.event().hostOrg!.itemId!;
            return StudentEngagementService.getLogoUrl(Item.Ids.Organization, hostOrgId).catch((error) => {
                throw error;
            });
        },
    });

    isoDate(): string {
        if (!this.event().date) return "";
        try {
            const date = S25Util.date.parse(this.event().date);
            return date.toISOString();
        } catch {
            return "";
        }
    }

    getDay(): string {
        if (!this.event().date) return "";
        try {
            const date = S25Util.date.parse(this.event().date);
            return date.getDate().toString();
        } catch {
            return "";
        }
    }

    getMonth(): string {
        if (!this.event().date) return "";
        try {
            const date = S25Util.date.parse(this.event().date);
            return S25Util.date.getMonthName(date).slice(0, 3);
        } catch {
            return "";
        }
    }

    attendanceStateLabel(state: AttendanceState | undefined): string {
        if (state === undefined) return "Set your attendance status for this event";
        if (Attendance.State.isAtLeast(state, attendanceState.CHECKED_IN)) {
            return "You have checked in to this event";
        }
        if (Attendance.State.isAtLeast(state, attendanceState.REGISTERED)) {
            return "You are registered for this event";
        }
        if (state === attendanceState.INTERESTED) {
            return "You are following this event";
        }
        return "Set your attendance status for this event";
    }
}
